import { keepPreviousData } from '@tanstack/react-query';
import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Activity,
  Building2,
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  Hash,
  Home,
  MessageSquare,
  MoreHorizontal,
  User,
} from 'lucide-react';
import { useState } from 'react';

import type { ListAllHomeCareReservations } from '@virtual-care/client';
import {
  displayName,
  formatDateTimeShort,
  formatReservationDateTime,
} from '@virtual-care/isomorphic';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
  cn,
} from '@virtual-care/shadcn';
import {
  RESERVATION_STATUS_META,
  ReservationStatus,
  SearchInput,
  TableRowSort,
  UserTypeBadge,
  useData,
} from '@virtual-care/ui';

import { CountablePageHeader } from '../../components/page-header.tsx';
import { Pagination } from '../../components/pagination.tsx';

type HomeCareReservationRecord = ListAllHomeCareReservations['records'][number];

const statusLabel = (status: string) =>
  RESERVATION_STATUS_META[status as keyof typeof RESERVATION_STATUS_META]?.en ??
  status;

type StatusFilterValue =
  | 'WAITING_CONFIRMATION'
  | 'CONFIRMED'
  | 'FINISHED'
  | 'CANCELED';

function getVendorDisplayName(
  vendor: HomeCareReservationRecord['vendor'],
): string {
  if (vendor.vendorType === 'INDIVIDUAL' && vendor.individualProfile) {
    const { firstName, lastName } = vendor.individualProfile;
    return `${firstName} ${lastName}`.trim();
  }
  if (vendor.vendorType === 'CLINIC' && vendor.clinicProfile) {
    return vendor.clinicProfile.clinicName;
  }
  return vendor.email;
}

export default function HomeCareAppointmentsPage() {
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'scheduledDate', desc: false },
  ]);
  const [searchQuery, setSearchQuery] = useState<string | undefined>();
  const [statusFilter, setStatusFilter] = useState<
    StatusFilterValue | undefined
  >();

  const [selectedReservation, setSelectedReservation] =
    useState<HomeCareReservationRecord | null>(null);
  const [showCaseDetails, setShowCaseDetails] = useState(false);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dataQuery = useData(
    'GET /homecare/all-reservations',
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      status: statusFilter,
      query: searchQuery || undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  const columns: ColumnDef<HomeCareReservationRecord>[] = [
    {
      accessorKey: 'counter',
      enableSorting: false,
      header: () => {
        return 'Case #';
      },
      cell: ({ row }) => (
        <div className="font-mono font-medium">#{row.getValue('counter')}</div>
      ),
    },
    {
      id: 'patientName',
      enableSorting: false,
      header: 'Patient Name',
      cell: ({ row }) => {
        return (
          <div className="text-sm font-medium">
            {displayName(row.original.patient)}
          </div>
        );
      },
    },
    {
      id: 'userType',
      enableSorting: false,
      header: 'Type',
      cell: ({ row }) => (
        <UserTypeBadge userType={row.original.patient.userType} />
      ),
    },
    {
      id: 'vendorName',
      enableSorting: false,
      header: 'Vendor',
      cell: ({ row }) => {
        const vendor = row.original.vendor;
        return (
          <div className="flex items-center gap-2">
            {vendor.vendorType === 'CLINIC' ? (
              <Building2 className="text-muted-foreground size-4" />
            ) : (
              <User className="text-muted-foreground size-4" />
            )}
            <span className="text-sm">{getVendorDisplayName(vendor)}</span>
          </div>
        );
      },
    },
    {
      id: 'service',
      enableSorting: false,
      header: 'Service',
      cell: ({ row }) => (
        <div className="font-medium">
          {row.original.vendorService.service.name}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      enableSorting: false,
      header: 'Status',
      cell: ({ row }) => {
        const reservation = row.original;

        return (
          <div className="flex items-center gap-2">
            <ReservationStatus.Root status={reservation.status} />
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      enableSorting: false,
      header: () => {
        return 'Created';
      },
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-2">
            <div className="text-sm">
              {formatDateTimeShort(row.original.createdAt)}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'scheduledDate',
      enableSorting: false,
      header: 'Scheduled Date',
      cell: ({ row }) => {
        const isCompletedState = ['FINISHED', 'CANCELED'].includes(
          row.original.status,
        );
        return (
          <div
            className={cn(
              'text-sm',
              isCompletedState && 'text-muted-foreground line-through',
            )}
          >
            {formatReservationDateTime(row.original)}
            {isCompletedState && (
              <div className="text-xs text-red-600 dark:text-red-400">
                {statusLabel(row.original.status)}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableSorting: false,
      cell: ({ row }) => {
        const reservation = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Case Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={() => {
                  setSelectedReservation(reservation);
                  setShowCaseDetails(true);
                }}
              >
                <Eye className="mr-2 h-4 w-4" />
                View case details
              </DropdownMenuItem>

              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigator.clipboard.writeText(reservation.id)}
                className="text-muted-foreground"
              >
                Copy case ID
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  const table = useReactTable({
    data: dataQuery.data?.records ?? [],
    columns,
    manualPagination: true,
    manualSorting: true,
    rowCount: dataQuery.data?.totalCount,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    state: {
      sorting,
      pagination,
    },
  });

  return (
    <div className="w-full">
      <CountablePageHeader
        title="HomeCare Appointments"
        totalCount={`${dataQuery.data?.totalCount || 0} total appointments`}
        icon={Home}
      />

      {dataQuery.data && (
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-yellow-600 dark:text-yellow-400" />
              <div>
                <div className="text-muted-foreground text-sm">
                  Waiting Confirmation
                </div>
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'WAITING_CONFIRMATION',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="size-4 text-green-600 dark:text-green-400" />
              <div>
                <div className="text-muted-foreground text-sm">Confirmed</div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'CONFIRMED',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
          <div className="bg-card border-border rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <Activity className="size-4 text-blue-600 dark:text-blue-400" />
              <div>
                <div className="text-muted-foreground text-sm">Finished</div>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {
                    dataQuery.data.records.filter(
                      (r) => r.status === 'FINISHED',
                    ).length
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex items-center gap-4">
        <div className="flex-1">
          <SearchInput
            placeholder="Search by patient name, phone, or description..."
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
            isLoading={dataQuery.isFetching}
          />
        </div>
        <Select
          value={statusFilter ?? 'all'}
          onValueChange={(v) =>
            setStatusFilter(v === 'all' ? undefined : (v as StatusFilterValue))
          }
        >
          <SelectTrigger className="min-h-11">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="WAITING_CONFIRMATION">
              {statusLabel('WAITING_CONFIRMATION')}
            </SelectItem>
            <SelectItem value="CONFIRMED">{statusLabel('CONFIRMED')}</SelectItem>
            <SelectItem value="FINISHED">{statusLabel('FINISHED')}</SelectItem>
            <SelectItem value="CANCELED">{statusLabel('CANCELED')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRowSort key={headerGroup.id} headerGroup={headerGroup} />
            ))}
          </TableHeader>
          <TableBody>
            {dataQuery.isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  Loading appointments...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No appointments found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Pagination
        currentPage={table.getState().pagination.pageIndex}
        pageSize={table.getState().pagination.pageSize}
        pageCount={table.getPageCount()}
        totalCount={table.getRowCount()}
        onPageChange={table.setPageIndex}
        onPageSizeChange={table.setPageSize}
      />

      {selectedReservation && (
        <HomeCareAppointmentDetailsDialog
          selectedReservation={selectedReservation}
          open={showCaseDetails}
          onOpenChange={setShowCaseDetails}
        />
      )}
    </div>
  );
}

// Dialog Components
interface HomeCareAppointmentDetailsDialogProps {
  selectedReservation: HomeCareReservationRecord;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const timelineTypeLabels = {
  CREATED: 'Reservation Created',
  CONFIRMED: 'Reservation Confirmed',
  FINISHED: 'Reservation Finished',
  CANCELED: 'Reservation Canceled',
};

const timelineTypeIcons = {
  CREATED: Calendar,
  CONFIRMED: CheckCircle,
  FINISHED: Activity,
  CANCELED: Clock,
};

function HomeCareAppointmentDetailsDialog({
  selectedReservation,
  open,
  onOpenChange,
}: HomeCareAppointmentDetailsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5" />
            Case #{selectedReservation.counter}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <User className="h-4 w-4" />
                Patient Information
              </h3>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Name: </span>
                  <span className="font-medium">
                    {displayName(selectedReservation.patient)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Email: </span>
                  <span>{selectedReservation.patient.email}</span>
                </div>
                {selectedReservation.patient.phoneNumber && (
                  <div>
                    <span className="text-muted-foreground">Phone: </span>
                    <span>{selectedReservation.patient.phoneNumber}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Home className="h-4 w-4" />
                Vendor Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Vendor: </span>
                  <span className="font-medium">
                    {getVendorDisplayName(selectedReservation.vendor)}
                  </span>
                  <Badge
                    variant={
                      selectedReservation.vendor.vendorType === 'CLINIC'
                        ? 'default'
                        : 'secondary'
                    }
                  >
                    {selectedReservation.vendor.vendorType === 'CLINIC'
                      ? 'Clinic'
                      : 'Individual'}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Email: </span>
                  <span>{selectedReservation.vendor.email}</span>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Activity className="h-4 w-4" />
                Case Information
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">Status: </span>
                  <ReservationStatus.Root status={selectedReservation.status} />
                </div>
                <div>
                  <span className="text-muted-foreground">Service: </span>
                  <span className="font-medium">
                    {selectedReservation.vendorService.service.name}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Created: </span>
                  <span>
                    {formatDateTimeShort(selectedReservation.createdAt)}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Scheduled: </span>
                  <span>
                    {formatReservationDateTime(selectedReservation)}
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <MessageSquare className="h-4 w-4" />
                Description
              </h3>
              <p className="text-muted-foreground warp-break-words text-sm whitespace-pre-wrap">
                {selectedReservation.description}
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="mb-3 flex items-center gap-2 font-semibold">
                <Clock className="h-4 w-4" />
                Case Timeline
              </h3>
              <div className="max-h-96 space-y-3 overflow-y-auto">
                {selectedReservation.timeline?.length > 0 ? (
                  selectedReservation.timeline.map((entry) => {
                    const IconComponent =
                      timelineTypeIcons[
                        entry.type as keyof typeof timelineTypeIcons
                      ] || Activity;
                    return (
                      <div
                        key={entry.id}
                        className="border-border flex gap-3 border-b pb-3 last:border-b-0"
                      >
                        <div className="flex-shrink-0">
                          <div className="bg-primary/10 flex h-8 w-8 items-center justify-center rounded-full">
                            <IconComponent className="text-primary h-4 w-4" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-1 flex items-center gap-2">
                            <span className="text-sm font-medium">
                              {timelineTypeLabels[
                                entry.type as keyof typeof timelineTypeLabels
                              ] || entry.type}
                            </span>
                            <span className="text-muted-foreground text-xs">
                              {formatDateTimeShort(entry.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-muted-foreground py-8 text-center">
                    <Activity className="mx-auto mb-2 h-8 w-8 opacity-50" />
                    <p className="text-sm">No timeline entries yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
